'use client';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import Box from '@mui/material/Box';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import Drawer from '@mui/material/Drawer';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useState, memo, Fragment } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { MobileSubnavItem } from './styles';
import MobileFavoriteLinks from './_MobileFavoriteLinks';
import MobileSubCategory from './_MobileSubCategory';
import UserAvatar from './_UserAvatar';
import MobileFeaturedContent from './_MobileFeaturedContent';

const MobileModal = ({
  categories,
  topMobileNavLinks,
  mobileMenuButtonLabel,
  backToMainMenuLabel,
}) => {
  const [show, setShow] = useState(false);
  const [category, setCategory] = useState(false);
  const [subCategory, setSubCategory] = useState(false);
  const { t } = useTranslation();

  const { openModalIds } = useUI();

  const categoryLink =
    subCategory?.heading?.link || subCategory?.heading?.externalLink
      ? buildLinkObj(subCategory.heading)
      : null;

  const handleClose = () => {
    reset();
    setShow(false);
  };

  const reset = () => {
    setCategory(false);
    setSubCategory(false);
  };

  return (
    <>
      <IconButton
        edge="start"
        aria-label={mobileMenuButtonLabel || 'Menu'}
        onClick={() => setShow(true)}
        size="large"
      >
        <MenuIcon aria-hidden sx={{ color: 'white' }} />
      </IconButton>
      <Drawer open={show && !openModalIds?.length} onClose={handleClose}>
        <Box
          pt={3}
          pb={4}
          sx={{
            width: '95vw',
            maxWidth: '24rem',
            height: '100%',
            maxHeight: '100%',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <Box display="flex" justifyContent="flex-end" pr={4}>
            <IconButton
              onClick={handleClose}
              aria-label={t('genericContent.closeIconButtonLabel', undefined, { default: 'Close' })}
              edge="end"
              size="large"
            >
              <CloseIcon aria-hidden />
            </IconButton>
          </Box>

          {(subCategory?.heading && category?.props) || category?.props?.dropdownName ? (
            <button
              className="tw-py-5 tw-px-7 tw-flex tw-items-center tra-text-web-heading-5 tw-leading-10"
              onClick={() => reset()}
              aria-label={backToMainMenuLabel || 'Back to Main Menu'}
            >
              <ChevronLeftIcon aria-hidden className="tw-mr-2" />

              {category.props.dropdownName}
            </button>
          ) : (
            topMobileNavLinks &&
            Array.isArray(topMobileNavLinks) && <MobileFavoriteLinks links={topMobileNavLinks} />
          )}
          <Box mb={2}>
            {!category &&
              categories &&
              Array.isArray(categories) &&
              categories.map((dropdownCategory, i) => (
                <Fragment key={i}>
                  {dropdownCategory.props && dropdownCategory.props.categoryLink ? (
                    dropdownCategory.props.categoryLink.map((navLink, i) => {
                      return (
                        <Anchor
                          className="tra-text-web-heading-5 tw-leading-10 tw-py-5 tw-px-7 tw-border-b"
                          key={i}
                          {...navLink}
                        >
                          {navLink.displayName}
                        </Anchor>
                      );
                    })
                  ) : (
                    <li className="tw-list-none">
                      <button
                        aria-label={dropdownCategory?.props?.dropdownName || 'Select Category'}
                        className="tw-flex tw-justify-between tw-items-center tw-w-full tw-py-5 tw-px-7 tw-border-b"
                        onClick={() => setCategory(dropdownCategory)}
                      >
                        <h5 className="tra-text-web-heading-5 tw-leading-10">
                          {dropdownCategory.props.dropdownName}
                        </h5>
                        <ChevronRightIcon aria-hidden />
                      </button>
                    </li>
                  )}
                </Fragment>
              ))}
            {!subCategory && (
              <>
                {category?.props?.links &&
                  Array.isArray(category.props.links) &&
                  category.props.links.map((dropdownSubCategory, i) => (
                    <MobileSubnavItem
                      key={i}
                      component="button"
                      type="button"
                      onClick={() => setSubCategory(dropdownSubCategory)}
                      aria-label={'Select Category'}
                      noBackground
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <li>{dropdownSubCategory.heading?.displayName}</li>
                        <ChevronRightIcon aria-hidden />
                      </Box>
                    </MobileSubnavItem>
                  ))}
                {category?.props?.featured?.image && (
                  <MobileFeaturedContent {...category.props.featured} />
                )}
              </>
            )}
            <MobileSubCategory subCategory={subCategory} categoryLink={categoryLink} />
          </Box>
          <div className="tw-mx-3">
            <UserAvatar dark showTextOnSmall full loggedOutText="Log In / Sign Up" />
          </div>
        </Box>
      </Drawer>
    </>
  );
};

export default memo(MobileModal);
